import {Component, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {PerFormaAPIService, PTPItemTypeDto} from "src/app/services/pfapi.service";
import {NzMessageService} from "ng-zorro-antd/message";

@Component({
    selector: "app-ptp-forms",
    templateUrl: "./ptp-forms.component.html",
    styleUrls: ["./ptp-forms.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PtpFormsComponent implements OnInit, OnDestroy {
    constructor(
        private route: ActivatedRoute,
        private api: PerFormaAPIService,
        private message: NzMessageService,
        private zone: NgZone
    ) {}

    ngOnInit(): void {
        // the form routes run in this page's <iframe> and report results by calling
        // window.parent.successAlert / errorAlert (see base-ptp-form). expose those here, bridged to
        // ng-zorro toasts, so the toast shows on the popup window like the legacy Forms host did.
        const win = window as any;
        win.successAlert = (m: string) => this.zone.run(() => this.message.success(m));
        win.errorAlert = (m: string) => this.zone.run(() => this.message.error(m));

        window.addEventListener(
            "message",
            event => {
                if (event?.data?.action === "LoadForm") {
                    this.LoadForm(event.data.form);
                }
            },
            false
        );

        this.route.queryParams.subscribe(prm => {
            if (prm.itemid) {
                this.ItemID = prm.itemid;
            }
            if (prm.itemtype) {
                this.ItemType = prm.itemtype;
            }
            if (prm.show) {
                this.Show = prm.show;
            }
            if (prm.formname) {
                this.ShowForm = prm.formname;
            }
        });

        this.api.getApiPTPGetForms({itemType: Number(this.ItemType) as PTPItemTypeDto}).subscribe((data: any[]) => {
            // Set scope forms.
            this.Forms = data.sort((s1, s2) => {
                // Ascending.
                return s1.Order - s2.Order;
            });

            // Set loading as false.
            this.Loading = false;

            // check to see if we ar calling for a specific form to be loaded first
            if (this.Forms && this.Forms.length > 0) {
                if (this.ShowForm?.length > 0) {
                    let found = false;

                    for (let i = 0; i < this.Forms.length; i++) {
                        const formName = (this.Forms[i].FormName + "").toLowerCase();

                        if (formName === this.ShowForm) {
                            found = true;
                            this.ShowForm = "";
                            this.LoadForm(this.Forms[i]);
                            break;
                        }
                    }

                    // if we get here, the user probably doesn't have access to the form (or it doesn't exist)
                    if (!found) {
                        this.ShowForm = "";
                        this.Show = "";
                        this.LoadForm(this.Forms[0]);
                    }
                } else {
                    this.LoadForm(this.Forms[0]);
                }
            }
        });

        // size the iframe to fit its content (ports the legacy Forms.cshtml resizer) so the popup
        // window scrolls and the inner page itself never shows a scrollbar.
        // we measure the form component host (display:block, sized to its content) rather than
        // body/documentElement.scrollHeight: those FLOOR at the iframe viewport, so adding a buffer to
        // them grows the frame every tick (the old runaway). the component host does not floor, so a
        // small buffer safely absorbs sub-pixel rounding without a feedback loop. non-form pages
        // (e.g. the Attach Invoice upload) fall back to an exact, buffer-free fit.
        this.resizeTimer = setInterval(() => {
            try {
                const frame = document.getElementById("iframeDiv") as HTMLIFrameElement;
                const doc = frame?.contentWindow?.document;
                if (!doc?.body) {
                    return;
                }

                const content = doc.querySelector(
                    "app-po, app-pr, app-order-monitor, app-goods-receipt, app-payment, app-audit-log, app-po-invoice"
                ) as HTMLElement;
                const contentHeight = content?.offsetHeight ?? 0;
                const target =
                    contentHeight > 0
                        ? contentHeight + 8
                        : Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);

                if (Math.abs(target - frame.clientHeight) > 1) {
                    frame.style.height = target + "px";
                }
            } catch {
                // iframe content not ready yet — ignore and retry on the next tick
            }
        }, 250);
    }

    ngOnDestroy(): void {
        clearInterval(this.resizeTimer);
        const win = window as any;
        delete win.successAlert;
        delete win.errorAlert;
    }

    private resizeTimer: ReturnType<typeof setInterval>;
    public frame: HTMLIFrameElement;
    // Set vars to necessary default values.
    Loading = true;
    FormVisible = false;
    Forms = [];
    FilePath = "";
    FrameOn = false;
    Show;
    ShowForm;
    ItemID;
    ItemType;
    SelectedForm = 0;

    GetClass = form => {
        if (form.FormID == this.SelectedForm) {
            return "active";
        } else return "";
    };

    LoadForm = (form, event?: Event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const frame = document.getElementById("iframeDiv") as HTMLIFrameElement;

        const isReload = (frame.src + "").length > 0;

        // if this ins't the first load of the form, ignore the show param
        if (isReload) {
            this.Show = null;
        }

        // if the form we're loading has these strings in it,
        // ignore it when firing the SaveForm message
        const saveIgnore = ["/upload", "auditlog"];

        /* Here we check if a tab is being navigated away from and
           then try to save the current state of that form.

           We have to make an exception if it's the "Attach Invoice" tab
           because it's a separately loaded aspx page and doesn't have a 
           'SaveForm' action, unlike the other Angular forms.
        */
        if (event && frame && !saveIgnore.some(i => frame.src.toLowerCase().indexOf(i) >= 0)) {
            frame.contentWindow.postMessage({action: "SaveForm", form: form});
            return;
        }

        setTimeout(() => {
            this.SelectedForm = form.FormID;
            let paramName = "poId";
            switch (this.ItemType) {
                case "0":
                    paramName = "poRequestId";
                    break;
                case "1":
                    paramName = "poId";
                    break;
            }

            const hasQuery = form.FormPath.indexOf("?") > -1;
            const includeType = form.FormPath.indexOf("/AuditLog") > -1;

            // force the item id param to be "poid" if we're using the AuditLog
            // due to how the PTPController looks for query params
            if (includeType) paramName = "poId";

            frame.src =
                form.FormPath +
                (hasQuery ? "&" : "?") +
                paramName +
                "=" +
                this.ItemID +
                (this.Show ? "&show=" + this.Show : "");

            frame.setAttribute("style", "width:100%;border:none;height:100%;");
        });
    };

    // Shows a success message.
    Success = message => {
        this.message.success(message);
    };
}
